import { useState } from 'react';
import { Check, Copy, KeyRound, Loader2, Settings, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Modal } from '@/components/modal';
import type {
  EmbedApiKey,
  EmbedKeySettings,
} from '@/services/external-chat/types';

/** Parses the comma-separated domains input into the API's `allowed_domains`
 *  shape: `null` (any origin allowed) when the field is left empty. */
const parseAllowedDomains = (raw: string): string[] | null => {
  const domains = raw
    .split(',')
    .map((d) => d.trim())
    .filter(Boolean);
  return domains.length > 0 ? domains : null;
};

/** Parses the rate-limit input into the API's `rate_limit_per_minute` shape:
 *  `null` (clear the override, i.e. platform default) when empty, otherwise a
 *  whole number of at least 1. Returns `undefined` for anything else, which the
 *  caller surfaces as a validation error rather than sending.
 *
 *  Two values must never reach the wire. `NaN`, because a `type="number"` input
 *  can still hand us "e" or "-" and `JSON.stringify` writes NaN as `null`,
 *  silently clearing the key's limit. And `0`, because `0` is the *stored*
 *  representation of "no override" — sending it would read as a deliberate cap
 *  of zero while actually applying the platform default, and a stored `0` then
 *  renders as an empty field. Clearing is what the empty field is for. */
const parseRateLimit = (raw: string): number | null | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed < 1) return undefined;
  return parsed;
};

interface KeyRowProps {
  keyRecord: EmbedApiKey;
  onRequestRevoke: (apiKey: string) => void;
  /** Resolves true when the settings persisted; false leaves the editor open. */
  onSaveSettings: (
    apiKey: string,
    settings: EmbedKeySettings,
  ) => Promise<boolean>;
}

const KeyRow = ({
  keyRecord,
  onRequestRevoke,
  onSaveSettings,
}: KeyRowProps) => {
  const [editing, setEditing] = useState(false);
  const [domainsInput, setDomainsInput] = useState(
    (keyRecord.allowed_domains || []).join(', '),
  );
  const [rateLimitInput, setRateLimitInput] = useState(
    keyRecord.rate_limit_per_minute
      ? String(keyRecord.rate_limit_per_minute)
      : '',
  );
  const [saving, setSaving] = useState(false);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);

  const handleSave = async () => {
    const rateLimit = parseRateLimit(rateLimitInput);
    if (rateLimit === undefined) {
      setRateLimitError(
        'Enter a whole number of 1 or more requests per minute, or leave empty for the default.',
      );
      return;
    }
    setRateLimitError(null);

    setSaving(true);
    try {
      const saved = await onSaveSettings(keyRecord.api_key, {
        allowed_domains: parseAllowedDomains(domainsInput),
        rate_limit_per_minute: rateLimit,
      });
      // Keep the editor open on failure so the entered values are not lost and
      // the row does not read as saved when the PATCH did not land.
      if (saved) setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='rounded-md border px-3 py-2 space-y-2'>
      <div className='flex items-center justify-between gap-2'>
        <div className='flex-1 min-w-0'>
          <code className='text-xs text-muted-foreground truncate block'>
            {keyRecord.api_key.slice(0, 24)}...
          </code>
          <span className='text-xs text-muted-foreground'>
            Created {new Date(keyRecord.created_at).toLocaleDateString()}
          </span>
        </div>
        <div className='flex items-center gap-1'>
          <Badge variant='outline' className='text-xs'>
            Active
          </Badge>
          <Button
            variant='ghost'
            size='icon'
            className='size-8'
            aria-label='Edit key settings'
            onClick={() => setEditing((v) => !v)}
          >
            <Settings className='size-3.5 text-muted-foreground' />
          </Button>
          <Button
            variant='ghost'
            size='icon'
            className='size-8'
            aria-label='Revoke key'
            onClick={() => onRequestRevoke(keyRecord.api_key)}
          >
            <Trash2 className='size-3.5 text-muted-foreground' />
          </Button>
        </div>
      </div>
      {editing && (
        <div className='space-y-3 border-t pt-2'>
          <div className='space-y-1'>
            <Label htmlFor={`domains-${keyRecord.api_key}`} className='text-xs'>
              Allowed Domains
            </Label>
            <Input
              id={`domains-${keyRecord.api_key}`}
              value={domainsInput}
              onChange={(e) => setDomainsInput(e.target.value)}
              placeholder='example.com, *.example.com'
              className='text-xs h-8'
            />
            <p className='text-xs text-muted-foreground'>
              Restrict which websites can use this key from a browser. Leave
              empty to allow any. Use *.example.com for subdomains.
            </p>
          </div>
          <div className='space-y-1'>
            <Label
              htmlFor={`rate-limit-${keyRecord.api_key}`}
              className='text-xs'
            >
              Rate Limit (requests/min)
            </Label>
            <Input
              id={`rate-limit-${keyRecord.api_key}`}
              type='number'
              min={1}
              step={1}
              value={rateLimitInput}
              onChange={(e) => {
                setRateLimitInput(e.target.value);
                setRateLimitError(null);
              }}
              placeholder='30 (default)'
              className='text-xs h-8'
              aria-invalid={rateLimitError ? true : undefined}
              aria-describedby={
                rateLimitError
                  ? `rate-limit-error-${keyRecord.api_key}`
                  : undefined
              }
            />
            {rateLimitError && (
              <p
                id={`rate-limit-error-${keyRecord.api_key}`}
                role='alert'
                className='text-xs text-destructive'
              >
                {rateLimitError}
              </p>
            )}
          </div>
          <Button size='sm' onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className='size-3.5 animate-spin mr-1' />}
            Save
          </Button>
        </div>
      )}
    </div>
  );
};

interface ManageKeysModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  keys: EmbedApiKey[];
  creatingKey: boolean;
  onCreateKey: () => void;
  onRequestRevoke: (apiKey: string) => void;
  /** Resolves true when the settings persisted; false leaves the editor open. */
  onSaveSettings: (
    apiKey: string,
    settings: EmbedKeySettings,
  ) => Promise<boolean>;
}

export const ManageKeysModal = ({
  open,
  onOpenChange,
  keys,
  creatingKey,
  onCreateKey,
  onRequestRevoke,
  onSaveSettings,
}: ManageKeysModalProps) => (
  <Modal
    isDialogOpen={open}
    setIsDialogOpen={onOpenChange}
    title={
      <span className='flex items-center gap-2'>
        <KeyRound className='size-5' />
        Embed API Keys
      </span>
    }
    description='Manage API keys used to authenticate the embed widget.'
    contentClassName='sm:max-w-lg'
    footer={
      <Button onClick={onCreateKey} disabled={creatingKey}>
        {creatingKey && <Loader2 className='size-4 animate-spin mr-1' />}
        Generate New Key
      </Button>
    }
  >
    <div className='space-y-3'>
      {keys.length === 0 ? (
        <p className='text-sm text-muted-foreground py-4 text-center'>
          No API keys yet. Generate one to enable the embed widget.
        </p>
      ) : (
        keys.map((key) => (
          <KeyRow
            key={key.api_key}
            keyRecord={key}
            onRequestRevoke={onRequestRevoke}
            onSaveSettings={onSaveSettings}
          />
        ))
      )}
    </div>
  </Modal>
);

interface RevealNewKeyModalProps {
  apiKey: string | null;
  keyCopied: boolean;
  onClose: () => void;
  onCopy: () => void;
}

export const RevealNewKeyModal = ({
  apiKey,
  keyCopied,
  onClose,
  onCopy,
}: RevealNewKeyModalProps) => (
  <Modal
    isDialogOpen={!!apiKey}
    setIsDialogOpen={(open) => {
      if (!open) onClose();
    }}
    title='Your new API key'
    description="Copy this key now. You won't be able to see the full key again."
    contentClassName='sm:max-w-md'
    footer={<Button onClick={onClose}>{keyCopied ? 'Done' : 'Close'}</Button>}
  >
    <div className='flex items-center gap-2'>
      <code className='flex-1 rounded-md bg-muted px-3 py-2 text-xs font-mono break-all select-all'>
        {apiKey}
      </code>
      <Button
        variant='outline'
        size='icon'
        className='shrink-0'
        onClick={onCopy}
      >
        {keyCopied ? <Check className='size-4' /> : <Copy className='size-4' />}
      </Button>
    </div>
  </Modal>
);

interface RevokeKeyModalProps {
  apiKey: string | null;
  onCancel: () => void;
  onConfirm: (apiKey: string) => void;
}

export const RevokeKeyModal = ({
  apiKey,
  onCancel,
  onConfirm,
}: RevokeKeyModalProps) => (
  <Modal
    isDialogOpen={!!apiKey}
    setIsDialogOpen={(open) => {
      if (!open) onCancel();
    }}
    title='Revoke API Key'
    description='This action cannot be undone. Any embeds using this key will stop working immediately.'
    contentClassName='sm:max-w-sm'
    footer={
      <div className='flex gap-2'>
        <Button variant='outline' onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant='destructive'
          onClick={() => {
            if (apiKey) onConfirm(apiKey);
          }}
        >
          Revoke Key
        </Button>
      </div>
    }
  />
);
